import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdUploadFile } from 'react-icons/md';
import Card from 'components/card/Card';
import FinanceFilters from 'components/finance/FinanceFilters';
import ImportTransactionsModal from 'components/finance/ImportTransactionsModal';
import TransactionForm from 'components/finance/TransactionForm';
import TransactionTable from 'components/finance/TransactionTable';
import { useFinance } from 'contexts/FinanceContext';
import { Transaction } from 'types/finance';

export default function Transactions() {
  const {
    filteredTransactions,
    deleteTransaction,
    toggleMarked,
    deleteMarked,
  } = useFinance();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isImportOpen,
    onOpen: onImportOpen,
    onClose: onImportClose,
  } = useDisclosure();
  const [editing, setEditing] = useState<Transaction | null>(null);
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const bgHover = useColorModeValue(
    { bg: 'secondaryGray.400' },
    { bg: 'whiteAlpha.50' }
  );

  const markedCount = filteredTransactions.filter((tx) => tx.marked).length;

  const handleEdit = (tx: Transaction) => {
    setEditing(tx);
    onOpen();
  };

  const handleAdd = () => {
    setEditing(null);
    onOpen();
  };

  const handleDelete = (id: string) => {
    deleteTransaction(id);
    toast({ title: 'Transaction deleted', status: 'info', duration: 3000 });
  };

  const handleDeleteMarked = () => {
    const removed = deleteMarked();
    toast({
      title: `${removed} marked transaction${removed === 1 ? '' : 's'} deleted`,
      status: 'info',
      duration: 3000,
    });
  };

  const handleClose = () => {
    setEditing(null);
    onClose();
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card mb="20px" p="20px">
        <Flex
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap="12px"
        >
          <Box>
            <Box color={textColor} fontSize="22px" fontWeight="700" mb="4px">
              Transactions
            </Box>
            <Box color="secondaryGray.600" fontSize="sm" fontWeight="500">
              Track, filter, and mark your income and expenses
            </Box>
          </Box>
          <Flex gap="10px" wrap="wrap">
            {markedCount > 0 && (
              <Button
                leftIcon={<Icon as={MdDelete} />}
                bg={boxBg}
                color="red.500"
                fontSize="sm"
                fontWeight="500"
                borderRadius="16px"
                h="40px"
                _hover={bgHover}
                onClick={handleDeleteMarked}
              >
                Delete Marked ({markedCount})
              </Button>
            )}
            <Button
              leftIcon={<Icon as={MdUploadFile} />}
              bg={boxBg}
              color={textColor}
              fontSize="sm"
              fontWeight="500"
              borderRadius="16px"
              h="40px"
              _hover={bgHover}
              onClick={onImportOpen}
            >
              Upload / Wallet
            </Button>
            <Button
              leftIcon={<Icon as={MdAdd} />}
              variant="brand"
              fontSize="sm"
              fontWeight="500"
              borderRadius="16px"
              px="24px"
              h="40px"
              onClick={handleAdd}
            >
              Add Transaction
            </Button>
          </Flex>
        </Flex>
      </Card>

      <FinanceFilters />

      <TransactionTable
        transactions={filteredTransactions}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleMarked={toggleMarked}
      />

      <TransactionForm
        isOpen={isOpen}
        onClose={handleClose}
        transaction={editing}
      />

      <ImportTransactionsModal isOpen={isImportOpen} onClose={onImportClose} />
    </Box>
  );
}
